import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import Container from "@/components/containers/Container";
import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import { useHasPermission } from "@/hooks/useHasPermission";
import type { ChildRouteContext } from "@/root";
import { throwIfHasNoPermission } from "@/utils/permissions";

import PositionLevelList from "@/modules/chart/admin/components/position-level/PositionLevelList";
import PositionList from "@/modules/chart/admin/components/position/PositionList";
import { fetchPositionsQueryOptions } from "@/modules/chart/admin/services";
import { ChartSelector } from "@/modules/chart/shared/components/ChartSelector";

export const Route = createFileRoute(
  "/admin/_panel/_chart/chart/$type/positions/",
)({
  beforeLoad: ({ context }) => {
    throwIfHasNoPermission(context.user, [
      "chart.chart-position.view",
      "chart.chart-position-level.view",
    ]);

    return {
      crumb: [
        {
          label: context.t("Positions", { ns: "chart.admin" }),
        },
      ],
    } satisfies ChildRouteContext;
  },
  loader: (opts) =>
    opts.context.queryClient.prefetchQuery(fetchPositionsQueryOptions()),
  component: Component,
});

function Component() {
  const { t } = useTranslation("chart.admin");
  const { type } = Route.useParams();
  const navigate = Route.useNavigate();

  const hasPermission = useHasPermission();

  return (
    <Container
      title={t("Manage Positions", { ns: "chart.admin" })}
      description={t("Manage positions in the organization", {
        ns: "chart.admin",
      })}
      action={
        <div className="lg:w-96">
          <ChartSelector
            value={type}
            onChange={(type) =>
              navigate({ to: Route.fullPath, params: { type } })
            }
          />
        </div>
      }
    >
      <TabGroup>
        <TabList>
          {hasPermission("chart.chart-position.view") && (
            <Tab>{t("Positions")}</Tab>
          )}
          {hasPermission("chart.chart-position-level.view") && (
            <Tab>{t("Position Levels")}</Tab>
          )}
        </TabList>
        <TabPanels>
          {hasPermission("chart.chart-position.view") && (
            <TabPanel>
              <PositionList type={type} />
            </TabPanel>
          )}
          {hasPermission("chart.chart-position-level.view") && (
            <TabPanel>
              <PositionLevelList type={type} />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>
    </Container>
  );
}
